import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import {
  FUNDRAISING_PRICING_REGISTRY,
  FUNDRAISING_COMPARISON_DEVELOPMENT_MODE,
  getAvailablePricingConfigurations,
} from '../../data/platform-pricing.config';
import { evidenceBadgeClasses } from '../../data/evidence-presentation';
import { configurationSummary as summarizeConfiguration } from '../../data/platform-result-presentation';
import type {
  FundraiserScenario,
  FundraisingComparisonState,
  PlatformComparisonResult,
  PlatformId,
  PlatformOptionChange,
  PricingSourceOpen,
} from '../../models/fundraising-comparison.models';
import { ComparisonAnalyticsService } from '../../services/comparison-analytics.service';
import { FundraisingCalculationCoordinator } from '../../services/fundraising-calculation-coordinator';
import { UrlScenarioStateService } from '../../services/url-scenario-state.service';
import { CalculationDetailsComponent } from '../calculation-details/calculation-details.component';
import { FundraiserScenarioComponent } from '../fundraiser-scenario/fundraiser-scenario.component';
import { PlatformOptionsComponent } from '../platform-options/platform-options.component';

interface ConfigurationComparisonViewModel {
  id: string;
  label: string;
  description: string;
  result: PlatformComparisonResult;
  organizationReceivesCents: number;
  organizationReceiveDeltaCents: number;
  visualizationPosition: number;
  visualizationStart: number;
  visualizationEnd: number;
  selected: boolean;
}

@Component({
  standalone: true,
  selector: 'fundraising-platform-calculator',
  imports: [
    CommonModule,
    FundraiserScenarioComponent,
    CalculationDetailsComponent,
    PlatformOptionsComponent,
  ],
  templateUrl: './fundraising-platform-calculator.component.html',
  styleUrls: [
    '../fundraising-comparison/fundraising-comparison.component.scss',
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FundraisingPlatformCalculatorComponent
  implements OnInit, OnDestroy
{
  @Input() platformId = '';

  readonly developmentMode = FUNDRAISING_COMPARISON_DEVELOPMENT_MODE;
  evidenceBadgeClasses = evidenceBadgeClasses;

  state!: FundraisingComparisonState;
  scenario?: FundraiserScenario;
  result?: PlatformComparisonResult;
  configurationResults: ConfigurationComparisonViewModel[] = [];
  intendedDonationBaseline = 0;
  minimumDisplayedAmount = 0;
  maximumDisplayedAmount = 0;
  baselineVisualizationPosition = 50;
  errorMessage = '';
  liveAnnouncement = '';
  copyStatus = '';

  private readonly calculationCoordinator =
    new FundraisingCalculationCoordinator();
  private urlUpdateTimeout?: ReturnType<typeof setTimeout>;
  private readonly popStateListener = () => this.restoreFromUrl();

  constructor(
    private readonly urlState: UrlScenarioStateService,
    private readonly analytics: ComparisonAnalyticsService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.state = this.urlState.read();
    if (!this.isAvailablePlatformId(this.platformId)) {
      this.errorMessage = this.platformId
        ? `The fundraising platform “${this.platformId}” is not available.`
        : 'This fundraising calculator is missing a platform ID.';
      this.cdr.markForCheck();
      return;
    }

    this.recalculate(false);
    window.addEventListener('popstate', this.popStateListener);
    this.analytics.track('fundraising_platform_calculator_loaded', {
      platform: this.platformId,
      page: 'platform-detail',
      pricing_mode: this.developmentMode ? 'development' : 'verified',
    });
  }

  ngOnDestroy(): void {
    window.removeEventListener('popstate', this.popStateListener);
    if (this.urlUpdateTimeout) clearTimeout(this.urlUpdateTimeout);
  }

  onIntendedGiftTotalCentsChange(intendedGiftTotalCents: number): void {
    this.state = { ...this.state, intendedGiftTotalCents };
    this.recalculate();
    this.trackScenarioChange();
  }

  onDonationCountOverrideChange(donationCountOverride: number | null): void {
    this.state = { ...this.state, donationCountOverride };
    this.recalculate();
    this.trackScenarioChange();
  }

  selectConfiguration(configuration: ConfigurationComparisonViewModel): void {
    if (configuration.selected) return;
    const platformId = this.platformId as PlatformId;
    const previousSelections = this.state.optionSelections[platformId] ?? {};
    const nextSelections = configuration.result.optionSelections;
    this.state = {
      ...this.state,
      optionSelections: {
        ...this.state.optionSelections,
        [platformId]: { ...nextSelections },
      },
    };
    this.recalculate();
    Object.entries(nextSelections).forEach(([optionGroupId, choiceId]) => {
      if (previousSelections[optionGroupId] === choiceId) return;
      this.analytics.track('fundraising_platform_option_changed', {
        platform: this.platformId,
        option_group: optionGroupId,
        choice: choiceId,
        page: 'platform-detail',
      });
    });
  }

  onOptionChange(change: PlatformOptionChange): void {
    const previousChoice =
      this.state.optionSelections[change.platformId]?.[change.optionGroupId];
    this.state = {
      ...this.state,
      optionSelections: {
        ...this.state.optionSelections,
        [change.platformId]: {
          ...this.state.optionSelections[change.platformId],
          [change.optionGroupId]: change.choiceId,
        },
      },
    };
    this.recalculate();
    if (previousChoice !== change.choiceId) {
      this.analytics.track('fundraising_platform_option_changed', {
        platform: change.platformId,
        option_group: change.optionGroupId,
        choice: change.choiceId,
        page: 'platform-detail',
      });
    }
  }

  onDetailsOpened(platformId: string): void {
    this.analytics.track('calculation_details_opened', {
      platform: platformId,
      page: 'platform-detail',
    });
  }

  onPricingSourceOpened(event: PricingSourceOpen): void {
    this.analytics.track('fundraising_pricing_source_opened', {
      platform: event.platformId,
      source_id: event.sourceId,
      page: 'platform-detail',
    });
  }

  onOverviewLinkClicked(): void {
    this.analytics.track('fundraising_platform_overview_link_clicked', {
      platform: this.platformId,
      page: 'platform-detail',
    });
  }

  async copyPlatformLink(): Promise<void> {
    try {
      await navigator.clipboard.writeText(
        this.urlState.buildAbsoluteUrl(this.state),
      );
      this.copyStatus = 'Platform scenario link copied.';
      this.analytics.track('fundraising_platform_link_copied', {
        platform: this.platformId,
        page: 'platform-detail',
      });
    } catch {
      this.copyStatus =
        'Unable to copy the link. Copy it from your browser address bar.';
    }
    this.cdr.markForCheck();
  }

  get overviewUrl(): string {
    const platformId = this.platformId as PlatformId;
    const selectedIds = new Set([...this.state.platformIds, platformId]);
    const overviewState = {
      ...this.state,
      platformIds: FUNDRAISING_PRICING_REGISTRY.canonicalPlatformOrder.filter(
        (id) => selectedIds.has(id),
      ),
    };
    return this.urlState.buildAbsoluteUrlForPath(
      overviewState,
      '/fundraising-platforms/',
    );
  }

  get organizationReceivesCents(): number {
    return Math.max(0, this.result?.organizationRetainedCents ?? 0);
  }

  get costsExceedDonationsCents(): number {
    return Math.max(0, -(this.result?.organizationRetainedCents ?? 0));
  }

  get configurationSummary(): string {
    return this.result ? summarizeConfiguration(this.result) : '';
  }

  get selectedConfigurationDescription(): string {
    return this.result ? this.configurationDescription(this.result) : '';
  }

  get showConfigurationComparison(): boolean {
    return (
      this.result?.configurationUi?.detailPresentation !== 'controls' &&
      this.configurationResults.length > 1
    );
  }

  get showConfigurationControls(): boolean {
    return (
      this.result?.configurationUi?.detailPresentation === 'controls' &&
      this.result.optionGroups.length > 0
    );
  }

  get organizationCostLabel(): string {
    const revenueShareItem = this.result?.feeLineItems.find(
      (item) =>
        item.includedInPrimaryResult &&
        item.paidBy === 'organization' &&
        item.category === 'revenue-share',
    );
    return revenueShareItem?.label.split(':')[0] ?? 'Organization pays';
  }

  get usesRevenueShare(): boolean {
    return this.organizationCostLabel !== 'Organization pays';
  }

  get grossAmountContext(): string {
    return this.usesRevenueShare
      ? 'gross fundraising proceeds'
      : 'intended online donations';
  }

  formatMoney(amountCents: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: amountCents % 100 === 0 ? 0 : 2,
    }).format(amountCents / 100);
  }

  formatDelta(amountCents: number): string {
    if (amountCents === 0) return '$0';
    const sign = amountCents > 0 ? '+' : '−';
    return `${sign}${this.formatMoney(Math.abs(amountCents))}`;
  }

  supporterExtraValue(result: PlatformComparisonResult): string {
    return result.donorPaidExtraEstimated
      ? this.formatMoney(result.donorPaidExtraCents)
      : 'Optional — not estimated';
  }

  formatPercentage(rateBasisPoints: number): string {
    return `${Number((rateBasisPoints / 100).toFixed(2))}%`;
  }

  tierAmountLabel(
    tier: NonNullable<
      PlatformComparisonResult['tieredPercentageScale']
    >['tiers'][number],
  ): string {
    const wholeDollarRanges =
      this.result?.tieredPercentageScale?.presentation.amountDisplay ===
      'whole-dollar-ranges';
    const displayAmount = (amountCents: number): number =>
      wholeDollarRanges ? Math.floor(amountCents / 100) * 100 : amountCents;
    if (tier.maxAmountCents === undefined) {
      return `${this.formatMoney(displayAmount(tier.minAmountCents))}+`;
    }
    return `${this.formatMoney(displayAmount(tier.minAmountCents))}–${this.formatMoney(
      displayAmount(tier.maxAmountCents),
    )}`;
  }

  private recalculate(updateUrl = true): void {
    if (!this.isAvailablePlatformId(this.platformId)) return;
    const snapshot = this.calculationCoordinator.calculate(this.state, [
      this.platformId,
    ]);
    this.state = snapshot.state;
    this.scenario = snapshot.scenario;
    this.result = snapshot.results[0];
    this.buildConfigurationResults();
    this.copyStatus = '';

    if (this.result) {
      this.liveAnnouncement = `${this.result.platformName} now receives ${this.formatMoney(
        this.organizationReceivesCents,
      )} from ${this.formatMoney(
        this.result.intendedGiftTotalCents,
      )} in intended online donations.`;
    }
    if (updateUrl) this.scheduleUrlUpdate();
    this.cdr.markForCheck();
  }

  private scheduleUrlUpdate(): void {
    if (this.urlUpdateTimeout) clearTimeout(this.urlUpdateTimeout);
    this.urlUpdateTimeout = setTimeout(() => {
      this.urlState.replace(this.state);
    }, 300);
  }

  private restoreFromUrl(): void {
    if (!this.isAvailablePlatformId(this.platformId)) return;
    this.state = this.urlState.read();
    this.recalculate(false);
  }

  private trackScenarioChange(): void {
    if (!this.scenario) return;
    this.analytics.track('fundraising_scenario_changed', {
      amount_bucket: this.analytics.amountBucket(
        this.state.intendedGiftTotalCents,
      ),
      donation_count_bucket: this.analytics.donationCountBucket(
        this.scenario.transactions.length,
      ),
      profile_id: this.state.profileId,
      donation_count_source: this.scenario.donationCountSource ?? 'profile',
      platform: this.platformId,
      page: 'platform-detail',
    });
  }

  private buildConfigurationResults(): void {
    if (!this.scenario || !this.result) {
      this.configurationResults = [];
      return;
    }

    const results = this.calculationCoordinator.calculatePlatformConfigurations(
      this.scenario,
      this.result.platformId,
    );
    this.intendedDonationBaseline = this.result.intendedGiftTotalCents;
    const receiptAmounts = results.map((result) =>
      Math.max(0, result.organizationRetainedCents),
    );
    const minimumAmount = Math.min(
      this.intendedDonationBaseline,
      ...receiptAmounts,
    );
    const maximumAmount = Math.max(
      this.intendedDonationBaseline,
      ...receiptAmounts,
    );
    const amountRange = maximumAmount - minimumAmount;
    const padding =
      amountRange > 0
        ? Math.max(1, amountRange * 0.15)
        : Math.max(100, this.intendedDonationBaseline * 0.05);
    this.minimumDisplayedAmount = minimumAmount - padding;
    this.maximumDisplayedAmount = maximumAmount + padding;
    this.baselineVisualizationPosition = this.getVisualizationPosition(
      this.intendedDonationBaseline,
    );

    this.configurationResults = results.map((result, index) => {
      const organizationReceivesCents = Math.max(
        0,
        result.organizationRetainedCents,
      );
      const visualizationPosition = this.getVisualizationPosition(
        organizationReceivesCents,
      );
      return {
        id: `configuration-${result.platformId}-${index}`,
        label: summarizeConfiguration(result) || result.platformName,
        description: this.configurationDescription(result),
        result,
        organizationReceivesCents,
        organizationReceiveDeltaCents:
          organizationReceivesCents - result.intendedGiftTotalCents,
        visualizationPosition,
        visualizationStart: Math.min(
          this.baselineVisualizationPosition,
          visualizationPosition,
        ),
        visualizationEnd: Math.max(
          this.baselineVisualizationPosition,
          visualizationPosition,
        ),
        selected: this.matchesSelectedConfiguration(result),
      };
    });
  }

  private getVisualizationPosition(amountCents: number): number {
    const displayedRange =
      this.maximumDisplayedAmount - this.minimumDisplayedAmount;
    if (displayedRange <= 0) return 50;
    return ((amountCents - this.minimumDisplayedAmount) / displayedRange) * 100;
  }

  private matchesSelectedConfiguration(
    candidate: PlatformComparisonResult,
  ): boolean {
    if (!this.result) return false;
    const candidateGroupIds = candidate.optionGroups.map((group) => group.id);
    const selectedGroupIds = this.result.optionGroups.map((group) => group.id);
    return (
      candidateGroupIds.join('|') === selectedGroupIds.join('|') &&
      candidate.optionGroups.every(
        (group) =>
          candidate.optionSelections[group.id] ===
          this.result?.optionSelections[group.id],
      )
    );
  }

  private configurationDescription(result: PlatformComparisonResult): string {
    return result.optionGroups
      .map((group) => {
        const selectedChoice = (group.choices ?? []).find(
          (choice) => choice.id === result.optionSelections[group.id],
        );
        return selectedChoice?.description ?? '';
      })
      .filter(Boolean)
      .join(' ');
  }

  private isAvailablePlatformId(platformId: string): platformId is PlatformId {
    return getAvailablePricingConfigurations().some(
      (platform) => platform.id === platformId,
    );
  }
}
